<template>
  <div class="revenue-table w-full border border-[#E0E0E04A] p-6 rounded-xl bg-[#F5F6FAB3]">
    <div>
      <template v-for="(group, groupType, groupIdx) in groupedRevenueItems" :key="groupType">
        <!-- Revenue Type Header -->
        <div class="grid grid-cols-4 items-center h-[38px] revenue-header rounded-t-[12px] mb-0.5">
          <div class="pl-4 font-['Open_Sans'] font-semibold text-[12px] leading-[105%] uppercase tracking-wider">{{ groupType }}</div>
          <div class="text-center font-['Open_Sans'] font-semibold text-[12px] leading-[105%] uppercase tracking-wider">Year 1 REVENUE</div>
          <div class="text-center font-['Open_Sans'] font-semibold text-[12px] leading-[105%] uppercase tracking-wider">Year 2 REVENUE</div>
          <div class="text-center font-['Open_Sans'] font-semibold text-[12px] leading-[105%] uppercase tracking-wider">Year 3 REVENUE</div>
        </div>
        <!-- Revenue Items -->
        <div v-for="(item, itemIndex) in group.items" :key="`${item.type}-${itemIndex}`" class="grid grid-cols-4 items-center h-[33px] bg-white divide-x divide-[#E0E0E04A] border-b border-[#E0E0E0]">
          <div class="pl-4 flex items-center w-full">
            <!-- For Other type, make name editable -->
            <input 
              v-if="groupType === 'Other'"
              v-model="item.name"
              type="text"
              class="w-full border-none bg-transparent focus:outline-none text-[#00423D] font-['Open_Sans'] text-[12px] leading-[105%] font-normal"
              placeholder="Enter item name..."
              @input="handleItemChange"
              @blur="handleItemBlur"
            />
            <!-- For other types, show name as text -->
            <span v-else class="text-[#00423D] font-['Open_Sans'] text-[12px] leading-[105%] font-normal">{{ item.name }}</span>
            <!-- Delete button for Other type -->
            <button 
              v-if="groupType === 'Other'"
              @click.stop="handleDeleteItem(groupType, itemIndex)"
              class="ml-auto mr-4 text-[#851208]"
            >
              <svg width="17" height="17" viewBox="0 0 11 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.5249 12.4498C2.17428 12.4498 1.87412 12.325 1.62443 12.0753C1.37475 11.8256 1.2499 11.5254 1.2499 11.1748V2.6748H0.399902V1.3998H3.7999V0.549805H7.1999V1.3998H10.5999V2.6748H9.7499V11.1672C9.7499 11.5264 9.62506 11.83 9.37537 12.0779C9.12568 12.3258 8.82553 12.4498 8.4749 12.4498H2.5249ZM8.4749 2.6748H2.5249V11.1748H8.4749V2.6748ZM3.7999 9.89981H5.0749V3.9498H3.7999V9.89981ZM5.9249 9.89981H7.1999V3.9498H5.9249V9.89981Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
          <div class="flex items-center justify-center">
            <input v-model.number="item.year1" type="number" step="0.01" min="0" inputmode="decimal" class="revenue-input" placeholder="" 
                   :disabled="isTypeLocked(item.type)"
                   :class="{ 'revenue-input--locked': isTypeLocked(item.type) }"
                   @keydown.arrow-up.prevent
                   @keydown.arrow-down.prevent
                   @wheel.prevent
                   @input="handleNumberInput($event, item, 'year1')"
                   @blur="handleItemBlur" />
          </div>
          <div class="flex items-center justify-center">
            <input v-model.number="item.year2" type="number" step="0.01" min="0" inputmode="decimal" class="revenue-input" placeholder="" 
                   :disabled="isTypeLocked(item.type)"
                   :class="{ 'revenue-input--locked': isTypeLocked(item.type) }"
                   @keydown.arrow-up.prevent
                   @keydown.arrow-down.prevent
                   @wheel.prevent
                   @input="handleNumberInput($event, item, 'year2')"
                   @blur="handleItemBlur" />
          </div>
          <div class="flex items-center justify-center">
            <input v-model.number="item.year3" type="number" step="0.01" min="0" inputmode="decimal" class="revenue-input" placeholder="" 
                   :disabled="isTypeLocked(item.type)"
                   :class="{ 'revenue-input--locked': isTypeLocked(item.type) }"
                   @keydown.arrow-up.prevent
                   @keydown.arrow-down.prevent
                   @wheel.prevent
                   @input="handleNumberInput($event, item, 'year3')"
                   @blur="handleItemBlur" />
          </div>
        </div>
        <!-- Add More Button for Other type - show even when empty -->
        <div v-if="groupType === 'Other'" class="pl-4 mb-2" :class="{ 'mt-2': group.items.length > 0 }">
          <button 
            @click.stop="handleAddItem"
            class="text-[#414141] text-[10px] leading-[105%] uppercase font-['Open_Sans'] font-semibold underline underline-offset-0"
          >
            <span v-if="group.items.length === 0">+ ADD NEW</span>
            <span v-else>+ ADD MORE</span>
          </button>
        </div>
        <!-- Section Total -->
        <div class="grid grid-cols-4 items-center h-[33px] section-total rounded-b-[12px]">
          <div class="text-right pl-8 font-['Open_Sans'] font-semibold text-[12px] leading-[105%] uppercase text-[#00423D]">Total</div>
          <div class="text-center font-['Open_Sans'] font-semibold text-[12px] leading-[105%] text-[#00423D]">${{ formatNumber(group.totals.year1) }}</div>
          <div class="text-center font-['Open_Sans'] font-semibold text-[12px] leading-[105%] text-[#00423D]">${{ formatNumber(group.totals.year2) }}</div>
          <div class="text-center font-['Open_Sans'] font-semibold text-[12px] leading-[105%] text-[#00423D]">${{ formatNumber(group.totals.year3) }}</div>
        </div>
        <!-- Spacer between groups -->
        <div v-if="groupIdx < Object.keys(groupedRevenueItems).length - 1" class="h-4"></div>
      </template>
    </div>
    <!-- Final Total Revenue -->
    <div class="grid grid-cols-4 items-center h-[38px] revenue-total-row rounded-b-xl mt-6">
      <div class="pl-8 text-right font-['Minion_Pro'] font-semibold text-[14px] leading-[105%] uppercase text-white">Total Revenue</div>
      <div class="text-center font-['Minion_Pro'] font-semibold text-[18px] leading-[105%] text-white">${{ formatNumber(totalRevenue.year1) }}</div>
      <div class="text-center font-['Minion_Pro'] font-semibold text-[18px] leading-[105%] text-white">${{ formatNumber(totalRevenue.year2) }}</div>
      <div class="text-center font-['Minion_Pro'] font-semibold text-[18px] leading-[105%] text-white">${{ formatNumber(totalRevenue.year3) }}</div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue';
import { safeSum, roundTwo } from '@/composables/numberUtils';

const props = defineProps({
  revenueItems: {
    type: Array,
    required: true,
    default: () => []
  },
  lockedRevenueTypes: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['data-changed', 'add-item', 'delete-item']);

// Auto-save debounce timer
let autoSaveTimer = null;
const AUTO_SAVE_DELAY = 1000; // 1 second delay

// Group revenue items by type and calculate subtotals
const groupedRevenueItems = computed(() => {
  const groups = {};
  props.revenueItems.forEach(item => {
    if (!groups[item.type]) {
      groups[item.type] = {
        items: [],
        totals: { year1: 0, year2: 0, year3: 0 }
      };
    }
    groups[item.type].items.push(item);
    groups[item.type].totals.year1 = safeSum(groups[item.type].totals.year1, item.year1);
    groups[item.type].totals.year2 = safeSum(groups[item.type].totals.year2, item.year2);
    groups[item.type].totals.year3 = safeSum(groups[item.type].totals.year3, item.year3);
  });
  
  // Ensure "Other" section exists even if empty, so users can add the first item
  if (!groups['Other']) {
    groups['Other'] = {
      items: [],
      totals: { year1: 0, year2: 0, year3: 0 }
    };
  }
  
  return groups;
});

// Calculate total revenue
const totalRevenue = computed(() => {
  return props.revenueItems.reduce((acc, item) => {
    acc.year1 = safeSum(acc.year1, item.year1);
    acc.year2 = safeSum(acc.year2, item.year2);
    acc.year3 = safeSum(acc.year3, item.year3);
    return acc;
  }, { year1: 0, year2: 0, year3: 0 });
});

const isTypeLocked = (type) => {
  return props.lockedRevenueTypes.includes(type);
};

// Format numbers with commas; hide decimals when value is integer
const formatNumber = (num) => {
  const n = roundTwo(num || 0);
  // If integer after rounding, don't show decimals
  if (Number.isInteger(n)) {
    return n.toLocaleString('en-US');
  }
  // Otherwise show up to 2 decimals (trailing zero trimmed by minimumFractionDigits: 0)
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

// Handle text input changes for Other type
const handleItemChange = () => {
  triggerAutoSave();
};

// Handle adding new item to Other type
const handleAddItem = () => {
  emit('add-item');
};

// Handle deleting item from Other type
const handleDeleteItem = (groupType, itemIndex) => {
  // Find the item in the grouped items
  const groupItems = groupedRevenueItems.value[groupType];
  if (!groupItems || !groupItems.items[itemIndex]) return;
  
  const itemToDelete = groupItems.items[itemIndex];
  
  // Find the actual index in the revenueItems array
  const actualIndex = props.revenueItems.findIndex(item => {
    // Match by reference first, then by type and name if available
    return item === itemToDelete || 
           (item.type === 'Other' && item.name === itemToDelete.name && 
            item.year1 === itemToDelete.year1 && 
            item.year2 === itemToDelete.year2 && 
            item.year3 === itemToDelete.year3);
  });
  
  if (actualIndex !== -1) {
    emit('delete-item', actualIndex);
  }
};

// Handle number input changes (limit to 2 decimal places)
const handleNumberInput = (event, item, field) => {
  if (isTypeLocked(item.type)) {
    return;
  }
  let raw = event.target.value;
  if (raw === '' || raw === null) {
    item[field] = 0;
    triggerAutoSave();
    return;
  }

  // Truncate fractional part to 2 digits if present
  const parts = String(raw).split('.');
  if (parts.length > 1) {
    parts[1] = parts[1].slice(0, 2);
    raw = parts[0] + '.' + parts[1];
    event.target.value = raw;
  }

  let value = parseFloat(String(raw).replace(/,/g, '')) || 0;
  if (value < 0) {
    value = Math.abs(value);
    event.target.value = String(value);
  }

  // Store rounded to 2 decimals to avoid floating precision issues
  item[field] = Math.round(value * 100) / 100;

  // Trigger auto-save
  triggerAutoSave();
};

// Handle input blur events
const handleItemBlur = () => {
  // Save immediately on blur
  clearTimeout(autoSaveTimer);
  triggerAutoSave(true);
};

// Trigger auto-save with debouncing
const triggerAutoSave = (immediate = false) => {
  // Clear existing timer
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }
  
  if (immediate) {
    // Save immediately
    emit('data-changed');
  } else {
    // Debounce the save
    autoSaveTimer = setTimeout(() => {
      emit('data-changed');
    }, AUTO_SAVE_DELAY);
  }
};

// Watch for external changes to revenue items (e.g., from API)
watch(() => props.revenueItems, () => {
  // Clear any pending auto-save when revenue items are updated externally
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }
}, { deep: true });

// Prevent negative values in the revenue table (keeping for backward compatibility)
const preventNegative = (event, item, year) => {
  const value = Number(event.target.value);
  if (value < 0) {
    event.target.value = Math.abs(value).toString();
    item[year] = Math.abs(value).toString();
  }
};
</script>

<style scoped>
.revenue-header {
  background: #00423D;
  color: #FFFFFF;
  letter-spacing: 1px;
}
.section-total {
  background: #FAF8F4;
  border-top: 1px solid #E0E0E0;
}
.revenue-total-row {
  background: #00423D;
  letter-spacing: 1px;
}
.revenue-input {
  width: 100%;
  background: transparent;
  border: none;
  text-align: center;
  color: #00423D;
  font-size: 12px;
  font-family: 'Open Sans', sans-serif;
  outline: none;
  padding: 4px 0;
}
.revenue-input--locked {
  color: #6B7280;
  cursor: not-allowed;
}
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
}
</style> 
